import { useTranslate } from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Input, Form, Row, Col, Tag, Image } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { EmotionListOut } from '@/api/generated/schemas';
import { ModStatus, EmotionKind } from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { BulkModStatusBar } from '@/components/user_catalog/BulkModStatusBar';
import { useBulkRowSelection } from '@/hooks/user_catalog/useBulkRowSelection';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const EmotionList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<EmotionListOut>({
      pagination: { pageSize: 20 },
      resource: 'user_catalog.emotions',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const selection = useBulkRowSelection<EmotionListOut>();

  return (
    <List>
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('user_catalog.emotions.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BulkModStatusBar
        resource="user_catalog.emotions"
        selection={selection}
      />

      <Table
        {...rowNav('user_catalog.emotions', tableProps)}
        rowKey="id"
        rowSelection={selection.rowSelection}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="icon_url"
          title={t('user_catalog.emotions.fields.icon_url')}
          render={(value: string) =>
            value ? (
              <Image
                src={value}
                height={32}
                style={{ objectFit: 'cover' }}
                preview={false}
              />
            ) : (
              '-'
            )
          }
        />

        <Table.Column
          dataIndex="name"
          title={t('user_catalog.emotions.fields.name')}
        />

        <Table.Column
          dataIndex="kind"
          title={t('user_catalog.emotions.fields.kind')}
          render={(value: string) =>
            value ? t(`user_catalog.emotions.kind.${value}`) : '-'
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'kind')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="kind"
              placeholder={t('user_catalog.emotions.fields.kind')}
              options={Object.values(EmotionKind).map((value) => ({
                label: t(`user_catalog.emotions.kind.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="is_promoted"
          title={t('user_catalog.emotions.fields.is_promoted')}
          render={(value: boolean) => (
            <Tag color={value ? 'green' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'is_promoted')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="is_promoted"
              placeholder={t('user_catalog.emotions.fields.is_promoted')}
              options={[
                { label: t('common.yes'), value: 'true' },
                { label: t('common.no'), value: 'false' },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="is_featured"
          title={t('user_catalog.emotions.fields.is_featured')}
          render={(value: boolean) => (
            <Tag color={value ? 'blue' : 'default'}>
              {value ? t('common.yes') : t('common.no')}
            </Tag>
          )}
          filtered={isFieldFiltered(filters as CrudFilter[], 'is_featured')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="is_featured"
              placeholder={t('user_catalog.emotions.fields.is_featured')}
              options={[
                { label: t('common.yes'), value: 'true' },
                { label: t('common.no'), value: 'false' },
              ]}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="search_popularity"
          title={t('user_catalog.common.fields.search_popularity')}
          sorter
          sortOrder={getSortOrder(sorters, 'search_popularity')}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'search_popularity'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="search_popularity"
              min={0}
              max={10}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(v: number) => v ?? 0}
        />

        <Table.Column
          dataIndex="mod_status"
          title={t('base.fields.mod_status')}
          render={(value: string) => <ModStatusTag value={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'mod_status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="mod_status"
              placeholder={t('base.fields.mod_status')}
              options={Object.values(ModStatus).map((value) => ({
                label: t(`base.mod_status.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: EmotionListOut) => (
            <Space>
              <EditButton hideText size="small" recordItemId={record.id} />
              <DeleteButton hideText size="small" recordItemId={record.id} />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
